// The styled dropdown every module-sub-controls picker uses (season / competition / team /
// template / metric / axis…) — main.js's window.renderStyledSelect ported to React in 05j-2
// (fork 1A). Why it exists at all (main.js): a native <select>'s open list is drawn by the
// OS and ignores author CSS (Safari worst), so it's a button showing the current value plus
// a plain positioned menu, styled like the player picker's.
//
// The same markup as main.js built (minus the blank text between tags, which shows
// nothing), the same classes styles.css targets. The clicks are plain listeners on the
// elements (fork 2A), like main.js's: each stops the click right where main.js stopped it,
// so no other listener sees a click it didn't see before. The page-wide rules (one open at a
// time, an outside click closes) are styled-select-menus.ts.

import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { LIVE } from "../live/flag.js";
import { closeOtherMenus, menuClosed, menuOpened } from "./styled-select-menus.js";
import {
  filterStyledSelectItems,
  findStyledSelectLabel,
  isGroup,
  type StyledSelectEntry,
} from "./styled-select-rules.js";

export type { StyledSelectGroup, StyledSelectItem } from "./styled-select-rules.js";

interface Props {
  items: readonly StyledSelectEntry[];
  value: string;
  onChange: (value: string) => void;
  /** The select's own class (e.g. "season-select") — on the button, so its width CSS applies. */
  extraClass?: string;
  /** class for the outer div (e.g. "season-select-wrap"). */
  wrapClass?: string;
  /** A search box at the top of the menu (long lists: Ranking's metrics, the axes, teams). */
  searchable?: boolean;
  /** What the closed button shows instead of the chosen item's label — a short form when the
   *  open list's labels are too long for the row (the Stats tile's competitions). */
  triggerLabel?: string;
}

function optionNodes(items: readonly StyledSelectEntry[], selected: string): ReactNode {
  if (!items.length) return <div className="styled-select-empty">No matches</div>;
  return items.map((it, i) =>
    isGroup(it) ? (
      <Fragment key={i}>
        <div className="styled-select-group-label">{it.group}</div>
        {optionNodes(it.items, selected)}
      </Fragment>
    ) : (
      <div key={i} className={`styled-select-option${it.value === selected ? " selected" : ""}`} data-value={it.value}>
        {it.icon}
        {it.label}
      </div>
    ),
  );
}

export function StyledSelect({ items, value, onChange, extraClass, wrapClass, searchable, triggerLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The listeners are added once; they read the latest values through these.
  const latest = useRef({ open, onChange });
  latest.current = { open, onChange };

  // main.js rebuilt the dropdown whenever these changed — closed, search emptied. Kept, so a
  // widget redraw with a new list shuts an open menu as it always did.
  useLayoutEffect(() => {
    setOpen(false);
    setQuery("");
    if (searchRef.current) searchRef.current.value = "";
  }, [items, value, extraClass, searchable]);

  // Live (Ben, 2026-10-16): a menu that would spill past its module's right edge opens to the
  // left instead (aligned to the trigger's right), and one wider than the module is capped to it —
  // otherwise the module scrolled sideways and everything slid out of view (Comparison's
  // Colour picker). The demo keeps its look.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const wrap = wrapRef.current;
    if (!LIVE || !open || !menu || !wrap) return;
    menu.style.left = "";
    menu.style.right = "";
    menu.style.maxWidth = "";
    const box = (wrap.closest(".placed-module") ?? document.body).getBoundingClientRect();
    const gap = 8;
    if (menu.getBoundingClientRect().right > box.right - gap) {
      menu.style.left = "auto";
      menu.style.right = "0";
    }
    if (menu.getBoundingClientRect().left < box.left + gap) {
      menu.style.maxWidth = `${Math.max(160, wrap.getBoundingClientRect().right - box.left - gap)}px`;
    }
  }, [open]);

  // Register while open, for the page-wide close rules.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!open || !wrap) return;
    menuOpened(wrap, () => setOpen(false));
    return () => menuClosed(wrap);
  }, [open]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const trigger = triggerRef.current;
    const optionsEl = optionsRef.current;
    if (!wrap || !trigger || !optionsEl) throw new Error("StyledSelect: markup missing");

    const onTrigger = (e: MouseEvent) => {
      e.stopPropagation();
      closeOtherMenus(wrap);
      const opening = !latest.current.open;
      // Synchronous, so the menu is open (and the search box exists) before focusing it —
      // main.js did all of this inside the click.
      flushSync(() => {
        setOpen(opening);
        // A fresh, unfiltered list every time it opens: a search left over from last time
        // would hide options the user never meant to exclude.
        if (opening) setQuery("");
      });
      const search = searchRef.current;
      if (opening && search) {
        search.value = "";
        search.focus();
      }
    };
    // Delegated on the list (main.js had one per option; nothing sits between an option
    // and the list, so it stops at the same point).
    const onOption = (e: MouseEvent) => {
      const opt = e.target instanceof Element ? e.target.closest<HTMLElement>(".styled-select-option") : null;
      if (!opt || !optionsEl.contains(opt)) return;
      e.stopPropagation();
      flushSync(() => setOpen(false));
      latest.current.onChange(opt.dataset.value ?? "");
    };
    trigger.addEventListener("click", onTrigger);
    optionsEl.addEventListener("click", onOption);
    return () => {
      trigger.removeEventListener("click", onTrigger);
      optionsEl.removeEventListener("click", onOption);
    };
  }, []);

  // The search box comes and goes with `searchable`, so its listeners follow it.
  useEffect(() => {
    const search = searchRef.current;
    if (!search) return;
    const stop = (e: MouseEvent) => e.stopPropagation();
    // Uncontrolled (as main.js's): a controlled input would also write a `value` attribute.
    const onInput = () => flushSync(() => setQuery(search.value));
    search.addEventListener("click", stop);
    search.addEventListener("input", onInput);
    return () => {
      search.removeEventListener("click", stop);
      search.removeEventListener("input", onInput);
    };
  }, [searchable]);

  const shown = filterStyledSelectItems(items, query);
  return (
    <div className={wrapClass}>
      <div className="styled-select-wrap" ref={wrapRef}>
        <button
          type="button"
          ref={triggerRef}
          className={`module-controls styled-select-trigger${extraClass ? ` ${extraClass}` : ""}`}
        >
          {triggerLabel ?? (findStyledSelectLabel(items, value) || "")}
        </button>
        <div className={open ? "styled-select-menu open" : "styled-select-menu"} ref={menuRef}>
          {searchable && (
            <input
              type="text"
              ref={searchRef}
              className="styled-select-search"
              placeholder="Search…"
              autoComplete="off"
              spellCheck={false}
            />
          )}
          <div className="styled-select-options" ref={optionsRef}>
            {optionNodes(shown, value)}
          </div>
        </div>
      </div>
    </div>
  );
}
